import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  Space,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { inverter_model_api } from '../api/inverter-model-api';

export default function InverterModels() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = async () => {
    try {
      setLoading(true);
      const res = await inverter_model_api.list();
      if (res.success) {
        setItems(res.data || []);
      }
    } catch (error) {
      message.error(error.message || 'Không tải được danh sách model');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 'active', manufacturer: 'Tosol' });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      manufacturer: record.manufacturer,
      ratedPowerW: record.ratedPowerW,
      description: record.description,
      status: record.status,
    });
    setOpen(true);
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);
      const payload = {
        ...values,
        code: String(values.code || '').trim().toUpperCase(),
      };
      const res = editing
        ? await inverter_model_api.update(editing.id, payload)
        : await inverter_model_api.create(payload);
      if (res.success) {
        message.success(editing ? 'Đã cập nhật model' : 'Đã tạo model');
        setOpen(false);
        await load();
      } else {
        message.error(res.message || 'Lưu thất bại');
      }
    } catch (error) {
      message.error(error.response?.data?.message || error.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Xóa model ${record.code}?`,
      content: 'Chỉ xóa được khi chưa có firmware gắn model này.',
      okType: 'danger',
      onOk: async () => {
        const res = await inverter_model_api.remove(record.id);
        if (res.success) {
          message.success('Đã xóa');
          await load();
        } else {
          message.error(res.message || 'Xóa thất bại');
        }
      },
    });
  };

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Model máy</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Thêm model
        </Button>
      </Space>
      <p style={{ color: '#666', marginBottom: 16 }}>
        Mỗi loại máy (vd. HYA-8K) có firmware / bootloader riêng. Bản firmware phải gắn đúng model.
      </p>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        pagination={{ pageSize: 15 }}
        columns={[
          { title: 'Mã', dataIndex: 'code', render: (v) => <strong>{v}</strong> },
          { title: 'Tên', dataIndex: 'name' },
          { title: 'Hãng', dataIndex: 'manufacturer' },
          {
            title: 'Công suất',
            dataIndex: 'ratedPowerW',
            render: (v) => (v != null ? `${v} W` : '—'),
          },
          {
            title: 'Trạng thái',
            dataIndex: 'status',
            render: (v) =>
              v === 'active' ? <Tag color="green">active</Tag> : <Tag>deprecated</Tag>,
          },
          { title: 'Mô tả', dataIndex: 'description', ellipsis: true },
          {
            title: '',
            key: 'actions',
            render: (_, record) => (
              <Space>
                <Button size="small" onClick={() => openEdit(record)}>
                  Sửa
                </Button>
                <Button size="small" danger onClick={() => handleDelete(record)}>
                  Xóa
                </Button>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        title={editing ? `Sửa ${editing.code}` : 'Thêm model máy'}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="code"
            label="Mã model"
            rules={[{ required: true, message: 'Nhập mã, vd HYA-8K' }]}
          >
            <Input placeholder="HYA-8K" disabled={editing?.code === 'TOSOL-HYBRID'} />
          </Form.Item>
          <Form.Item name="name" label="Tên" rules={[{ required: true }]}>
            <Input placeholder="Tosol Hybrid 8K" />
          </Form.Item>
          <Form.Item name="manufacturer" label="Hãng">
            <Input />
          </Form.Item>
          <Form.Item name="ratedPowerW" label="Công suất định mức (W)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="Trạng thái">
            <Select
              options={[
                { value: 'active', label: 'active' },
                { value: 'deprecated', label: 'deprecated' },
              ]}
            />
          </Form.Item>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving} block>
            Lưu
          </Button>
        </Form>
      </Modal>
    </div>
  );
}
